import { tt } from '@agos/shared'

/**
 * `/cms` — the pages the customer site serves, and what the manager may change about them.
 *
 * Two findings, both measured against the restored dumps:
 *
 * - **FB-124** the list falls back to six hard-coded pages and calls them *a local copy*
 * - **FB-125** the slug is editable, and saving a changed one creates a second page
 */

// The eight fields this declared are the backend's now (BLOCKED #4), and required rather than
// optional: every column on `cms_pages` is NOT NULL, so a page that came back has all of them.
import type { CmsPage } from '@agos/api-client/model'

export type { CmsPage }

/**
 * A page being written, which is not yet a page the API answered.
 *
 * `CmsPage` means *a row that came back* — every column on `cms_pages` is NOT NULL, so all eight
 * fields are guaranteed. A new page has none of them, and `draftOf({})` is exactly that call.
 */
export type CmsPageDraft = Partial<CmsPage>

export interface PageDraft {
  slug: string
  title: string
  content: string
  visible: boolean
  metaTitle: string
  metaDesc: string
}

export const EMPTY_DRAFT: PageDraft = {
  slug: '',
  title: '',
  content: '',
  visible: false,
  metaTitle: '',
  metaDesc: '',
}

export function draftOf(page: CmsPageDraft): PageDraft {
  return {
    slug: page.slug ?? '',
    title: page.title ?? '',
    content: page.content ?? '',
    visible: page.visible === true,
    metaTitle: page.metaTitle ?? '',
    metaDesc: page.metaDesc ?? '',
  }
}

/** Exactly the body `PUT /cms-pages/{slug}` reads. The slug is the path, never the body. */
export function savePayload(draft: PageDraft): {
  title: string
  content: string
  visible: boolean
  metaTitle: string
  metaDesc: string
} {
  return {
    title: draft.title.trim(),
    content: draft.content,
    visible: draft.visible,
    metaTitle: draft.metaTitle.trim(),
    metaDesc: draft.metaDesc.trim(),
  }
}

// ─── FB-124: what a failed load must not pretend ─────────────────────────────

/**
 * **FB-124.** Legacy seeds its state from `localStorage`, falling back to six hard-coded pages.
 *
 * ```tsx
 * const [pages, setPages] = useState<CmsPage[]>(loadFromLocalStorage);
 * // …
 * catch (err) { setError("Nepodarilo sa načítať stránky zo servera. Zobrazujem lokálnu kópiu.") }
 * ```
 *
 * So when `/cms-pages/admin/all` fails, the screen keeps six pages with working *edit*, *delete* and
 * *hide* buttons and calls them a local copy. On this server the two lists do not match:
 *
 * | | Slugs |
 * |---|---|
 * | server (5) | `home`, `kontakt`, `vseobecne-podmienky`, `alergeny`, **`ochrana-osobnych-udajov`** |
 * | hard-coded (6) | `home`, `kontakt`, `vseobecne-podmienky`, `alergeny`, **`o-nas`**, **`gdpr`** |
 *
 * Two of the six — `o-nas` and `gdpr` — **have never existed here**, and the one real page they do not
 * cover, `ochrana-osobnych-udajov`, is the site's privacy notice. The fallback also carries meta titles
 * and descriptions for all six, where four of the five real pages have none, so the fictional list looks
 * healthier than the real one.
 *
 * A copy of nothing is not a copy. When the load fails this page shows the failure.
 */
export const LEGACY_FALLBACK_SLUGS: readonly string[] = [
  'home',
  'o-nas',
  'kontakt',
  'gdpr',
  'vseobecne-podmienky',
  'alergeny',
]

// ─── FB-125: the slug is the identity ────────────────────────────────────────

/**
 * **FB-125.** `PUT /cms-pages/{slug}` is an upsert on the slug:
 *
 * ```sql
 * INSERT INTO cms_pages (slug, title, content, visible, meta_title, meta_desc, updated_at)
 * VALUES (:slug, …) ON CONFLICT (slug) DO UPDATE SET title = EXCLUDED.title, …
 * ```
 *
 * Legacy's editor lets the slug be typed over and then PUTs to whatever it now says. Renaming
 * `kontakt` to `kontakty` therefore **inserts a second page** and leaves the first one published; the
 * list looks correct afterwards, because the local row is replaced by its old `id`, until somebody
 * reloads and finds both. The customer site keeps serving `/kontakt`.
 *
 * There is no rename endpoint. So on an existing page the slug is read-only and says why; a new page
 * chooses one once.
 */
export function slugIsEditable(page: CmsPageDraft | null): boolean {
  return page === null
}

const SLUG_SHAPE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

export type SlugProblem = 'empty' | 'shape' | 'taken' | null

/**
 * Whether a new page's slug can be used.
 *
 * The shape rule is the customer site's URL, so it is lower-case letters, digits and single hyphens —
 * a slug with an upper-case letter or a space produces a link nobody can type. `taken` matters because
 * the endpoint would silently overwrite the existing page rather than refusing.
 */
export function slugProblem(slug: string, existing: CmsPageDraft[]): SlugProblem {
  const value = slug.trim()
  if (value === '') return 'empty'
  if (!SLUG_SHAPE.test(value)) return 'shape'
  if (existing.some((page) => (page.slug ?? '') === value)) return 'taken'
  return null
}

export function slugProblemMessage(problem: SlugProblem): string {
  if (problem === 'empty') return tt('Adresa je povinná.')
  if (problem === 'shape') {
    return tt('Adresa smie mať len malé písmená, číslice a spojovníky - napríklad o-nas.')
  }
  if (problem === 'taken') {
    return tt('Stránka s touto adresou už existuje. Uložením by ste ju prepísali.')
  }
  return ''
}

/** A title typed into the new-page form, turned into a slug worth suggesting. */
export function slugFromTitle(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

// ─── what the SEO fields are for ─────────────────────────────────────────────

export const META_TITLE_MAX = 60
export const META_DESC_MAX = 160

export interface SeoState {
  titleLength: number
  descLength: number
  titleMissing: boolean
  descMissing: boolean
  titleTooLong: boolean
  descTooLong: boolean
}

export function seoState(draft: PageDraft): SeoState {
  const titleLength = draft.metaTitle.trim().length
  const descLength = draft.metaDesc.trim().length
  return {
    titleLength,
    descLength,
    titleMissing: titleLength === 0,
    descMissing: descLength === 0,
    titleTooLong: titleLength > META_TITLE_MAX,
    descTooLong: descLength > META_DESC_MAX,
  }
}

/**
 * How many published pages have nothing for a search engine to show.
 *
 * Measured: **four of the five live pages have neither a meta title nor a meta description** — only
 * `ochrana-osobnych-udajov` has both. Legacy counts characters inside the editor and says nothing at
 * the list level, so the only way to learn this is to open all five.
 */
export function pagesWithoutSeo<T extends CmsPageDraft>(pages: T[]): T[] {
  return pages.filter(
    (page) => (page.metaTitle ?? '').trim() === '' || (page.metaDesc ?? '').trim() === '',
  )
}

// ─── the content field ───────────────────────────────────────────────────────

export interface ContentShape {
  characters: number
  looksLikeHtml: boolean
  hasScript: boolean
  hasStyle: boolean
}

/**
 * What is in the content box.
 *
 * Legacy's help text promises HTML, CSS and `<script>` are supported and that the customer site runs
 * them in a sandboxed iframe. That is the customer app's contract to keep; this page's job is to say
 * plainly what it is about to store, because a `<script>` saved here runs in front of customers.
 */
export function contentShape(content: string): ContentShape {
  const lower = content.toLowerCase()
  return {
    characters: content.length,
    looksLikeHtml: /<[a-z][\s\S]*>/i.test(content),
    hasScript: lower.includes('<script'),
    hasStyle: lower.includes('<style'),
  }
}

// ─── the list ────────────────────────────────────────────────────────────────

export function matchesSearch(page: CmsPageDraft, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (needle === '') return true
  return [page.title, page.slug, page.metaTitle].some((field) =>
    (field ?? '').toLowerCase().includes(needle),
  )
}

/** Visible pages first, then by title — the order somebody scanning for a live page wants. */
export function orderPages<T extends CmsPageDraft>(pages: T[]): T[] {
  return [...pages].sort(
    (left, right) =>
      Number(right.visible === true) - Number(left.visible === true) ||
      (left.title ?? '').localeCompare(right.title ?? '', 'sk'),
  )
}

export function formatUpdated(value: string | undefined): string {
  const text = (value ?? '').trim()
  if (text === '') return '-'
  return text.slice(0, 10)
}

export interface PageCounts {
  total: number
  visible: number
  hidden: number
  withoutSeo: number
}

export function pageCounts(pages: CmsPageDraft[]): PageCounts {
  return {
    total: pages.length,
    visible: pages.filter((page) => page.visible === true).length,
    hidden: pages.filter((page) => page.visible !== true).length,
    withoutSeo: pagesWithoutSeo(pages).length,
  }
}

/**
 * Whether deleting this page would take a link the customer site relies on.
 *
 * These four are linked from the customer app's footer and, in the case of the terms, from the order
 * confirmation. `DELETE /cms-pages/{slug}` removes the row with no warning and no undo, and legacy
 * asks nothing before calling it.
 */
export const LINKED_FROM_CUSTOMER_SITE: readonly string[] = [
  'home',
  'kontakt',
  'vseobecne-podmienky',
  'ochrana-osobnych-udajov',
]

export function deleteIsRisky(page: CmsPageDraft): boolean {
  return LINKED_FROM_CUSTOMER_SITE.includes(page.slug ?? '')
}
